import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Badge, Col, Button, Modal, Form } from "react-bootstrap";
import PageTitle from "../layouts/PageTitle";
import Spinner from "../common/Spinner";
import { notifyError, notifyTopRight } from "../common/Toaster";
import {
  listCases,
  listRuns,
  listCompanies,
  run as runCases,
  getRun,
  presignFixtureUpload,
  putFileToS3,
  createCase,
  updateCase,
  approveRun,
} from "../../services/OcrTestSuite/OcrTestSuiteService";
import { getClassifierConfig } from "../../services/Classifier/ClassifierService";

// OCR Test Suite — Admin UI (PLAN-ocr-test-suite.md §7, Stage 3–4).
// Pattern-copied from ErrorTelemetry.js (Card/Table/Badge, list <-> detail via a
// `view` state). Backend routes exist only when ENABLE_OCR_TEST_SUITE === 'true'.
//
// Built: List screen + coverage matrix + run loop (fire POST /run, poll
// GET /runs/:id) + New/Edit Test Case modal. Run Detail sub-view = step 6.

// Scalar `expected` fields offered directly in the modal (PLAN §3 field scope).
// items[] / variance are authored via "Approve as Expected" after a run.
const EXPECTED_MONEY_FIELDS = [
  ["subTotal", "Subtotal"],
  ["totalTax", "Total tax"],
  ["invoiceTotal", "Invoice total"],
  ["gst", "GST"],
  ["pst", "PST"],
  ["hst", "HST"],
];
// Test-suite-only field, not persisted on the real IReceipt — the run engine
// derives it from Layer 0 (Azure's raw docType) and scores it only against
// Layer 2a, not Layer 2b (mobile never renders/displays doc type).
const DOC_TYPES = ["", "Receipt", "Invoice"];

// Detail view — bucket a diff entry's `field` into a display group (PLAN §7).
const fieldGroup = (field) => {
  if (field.startsWith("items") || field.startsWith("differentialItem"))
    return "Items";
  if (["subTotal", "invoiceTotal"].includes(field)) return "Totals";
  if (
    field.startsWith("gst") ||
    field.startsWith("pst") ||
    field.startsWith("hst") ||
    field === "totalTax" ||
    field.startsWith("variance")
  )
    return "Tax";
  return "Header"; // vendor, date, docId, classificationRoutingPath, scenario, …
};
const GROUP_ORDER = ["Header", "Items", "Totals", "Tax"];

const fmtVal = (v) => {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// The 12 scenario codes the coverage matrix rows are drawn from (PLAN §2b).
const SCENARIO_CODES = [
  "1",
  "2",
  "3a",
  "3b",
  "4a",
  "4c",
  "4d",
  "4e",
  "6a",
  "6b",
  "6c",
  "7a",
];

// Short human-readable description per scenario code, traced from
// extractDataFromInvoiceOrReceipt.ts's scenario-assignment logic.
const SCENARIO_DESCRIPTIONS = {
  "1": "Inclusive",
  "2": "Exclusive",
  "3a": "Differential — inclusive",
  "3b": "Differential — exclusive",
  "4a": "No tax — matching",
  "4c": "No tax — not matching",
  "4d": "No tax, no total",
  "4e": "No tax, no total, but vendor Tax ID",
  "6a": "Tax, no invoice total — calculates to inclusive",
  "6b": "Tax, no invoice total — calculates to exclusive",
  "6c": "Tax, no invoice total — can't identify as inclusive or exclusive",
  "7a": "No line items",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString();
};

const statusBadge = (status) => {
  if (!status) return <Badge variant="light">never run</Badge>;
  const map = {
    pass: "success",
    fail: "danger",
    running: "warning",
    errored: "dark",
  };
  return <Badge variant={`${map[status] || "light"} light`}>{status}</Badge>;
};

// Reduce a case's run history to a single last-run status token.
const lastRunStatus = (runsForCase) => {
  if (!runsForCase || runsForCase.length === 0) return null;
  const latest = runsForCase[0];
  if (latest.status === "running") return "running";
  if (latest.status === "errored") return "errored";
  return latest.overallPass ? "pass" : "fail";
};

// Did the latest run's extracted receipt come back flagged for tax review?
// (`needsTaxReview` is a real IReceipt field surfaced by getReceiptById — the
// CA "tax couldn't be auto-resolved" signal. See PLAN §2b.)
const latestRunNeedsTaxReview = (runsForCase) => {
  if (!runsForCase || runsForCase.length === 0) return false;
  const latest = runsForCase[0];
  return latest?.hub?.savedReceiptRecord?.needsTaxReview === true;
};

export default function OcrTestSuite() {
  const [loader, setLoader] = useState(false);
  const [view, setView] = useState("list"); // "list" | "detail"

  // detail view
  const [detailCaseId, setDetailCaseId] = useState(null);
  const [detailRunId, setDetailRunId] = useState(null); // which run is shown; null = latest
  const [detailTab, setDetailTab] = useState("mobile"); // "extraction" | "conversion" | "mobile"
  const [rawOpen, setRawOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const [cases, setCases] = useState([]);
  const [runs, setRuns] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [classifierConfig, setClassifierConfig] = useState(null); // { killSwitch, confidenceThreshold, timeout }

  // filters
  const [regionFilter, setRegionFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all"); // header company filter — filter only (PLAN decision 2)
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pass" | "fail" | "neverRun" — driven by the stats cards

  // run loop
  const [selected, setSelected] = useState([]); // testCaseIds
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState(""); // human-readable status line

  // new/edit test case modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null); // null = new
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // { name, description, ocrTestCompanyId, scenarioTag, file, expected:{}, classificationRoutingPath }

  const fetchAll = async () => {
    setLoader(true);
    try {
      const [casesRes, runsRes, companiesRes, configRes] = await Promise.all([
        listCases(),
        listRuns({ limit: 200 }),
        listCompanies(),
        getClassifierConfig(),
      ]);
      setCases(casesRes.data.data || []);
      setRuns(runsRes.data.data || []);
      setCompanies(companiesRes.data.data || []);
      setClassifierConfig(configRes.data.data || null);
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          "Failed to load OCR test suite (is ENABLE_OCR_TEST_SUITE set?)"
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ── detail view helpers ─────────────────────────────────────────────────
  const openDetail = (testCaseId) => {
    setDetailCaseId(String(testCaseId));
    setDetailRunId(null);
    setDetailTab("mobile");
    setRawOpen(false);
    setView("detail");
  };
  const backToList = () => {
    setView("list");
    setDetailCaseId(null);
    setDetailRunId(null);
  };

  // ── run loop: fire POST /run, then poll GET /runs/:id until status !== 'running' ──
  // Bounded on every axis so a failure path always ENDS in a visible signal, never
  // a pending promise: an overall timeout, and a consecutive-error cap for a
  // persistent getRun failure (expired session, deleted run row, network down).
  // On exhaustion it resolves an explicit errored-run object (not null) that the
  // caller treats exactly like a real 'errored' run and surfaces to the admin.
  const POLL_INTERVAL_MS = 2500;
  const POLL_MAX_MS = 60 * 1000; // ~60s — an OCR run is normally < 5s
  const POLL_MAX_CONSECUTIVE_ERRORS = 20;
  const pollRun = (runId) =>
    new Promise((resolve) => {
      const startedAt = Date.now();
      let consecutiveErrors = 0;
      let lastErrorMessage = "";
      const giveUp = (why) =>
        resolve({
          _pollFailed: true,
          status: "errored",
          errorMessage: why,
        });
      const tick = async () => {
        if (Date.now() - startedAt > POLL_MAX_MS) {
          giveUp(
            `Stopped waiting for run ${String(runId).slice(
              -6
            )} after ${Math.round(POLL_MAX_MS / 1000)}s — open Run Detail to see if it finished.`
          );
          return;
        }
        try {
          const res = await getRun(runId);
          consecutiveErrors = 0;
          const doc = res.data.data;
          if (doc && doc.status && doc.status !== "running") {
            resolve(doc);
            return;
          }
        } catch (err) {
          console.log("pollRun error", err);
          lastErrorMessage =
            err?.response?.data?.message || err?.message || "network error";
          consecutiveErrors += 1;
          if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) {
            giveUp(
              `Lost contact with the server while polling run ${String(
                runId
              ).slice(-6)} (${lastErrorMessage}).`
            );
            return;
          }
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      };
      tick();
    });

  const handleRunSelected = async (explicitIds) => {
    const ids = Array.isArray(explicitIds) ? explicitIds : selected;
    if (ids.length === 0 || running) return;
    setRunning(true);
    setRunProgress(`Starting ${ids.length} run(s)…`);
    try {
      const res = await runCases({ caseIds: ids });
      const body = res.data.data || {};
      const runIds = body.runIds || (body.runId ? [body.runId] : []);
      if (runIds.length === 0) {
        notifyError("Run started but no run id was returned");
        setRunning(false);
        return;
      }
      let done = 0;
      let failed = 0;
      const pollFailureMessages = [];
      for (const id of runIds) {
        setRunProgress(`Running ${done + 1} / ${runIds.length}…`);
        const doc = await pollRun(id);
        done += 1;
        if (!doc || doc.status === "errored" || doc.overallPass === false) {
          failed += 1;
          // A poll that gave up carries its own explanatory message — surface it.
          if (doc && doc._pollFailed && doc.errorMessage) {
            pollFailureMessages.push(doc.errorMessage);
          }
        }
      }
      setRunProgress("");
      if (failed === 0) {
        notifyTopRight(`All ${runIds.length} run(s) passed`);
      } else if (pollFailureMessages.length > 0) {
        // At least one failure was "lost contact", not a real fail result.
        notifyError(
          `${failed} of ${runIds.length} run(s) did not complete cleanly. ${pollFailureMessages[0]}`
        );
      } else {
        notifyError(`${failed} of ${runIds.length} run(s) failed`);
      }
      setSelected([]);
      await fetchAll();
    } catch (err) {
      setRunProgress("");
      const status = err?.response?.status;
      if (status === 409) {
        notifyError("A test run is already in progress — try again shortly");
      } else {
        notifyError(
          err?.response?.data?.message || "Failed to start test run"
        );
      }
    } finally {
      setRunning(false);
    }
  };

  const toggleSelected = (id) => {
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : cur.concat(id)
    );
  };

  // ── new/edit test case modal ────────────────────────────────────────────
  const openNewCase = () => {
    setEditingCase(null);
    setForm({
      name: "",
      description: "",
      ocrTestCompanyId: "", // required, never defaults to the header filter (PLAN decision 2)
      scenarioTag: "",
      file: null,
      expected: {},
      docType: "",
    });
    setModalOpen(true);
  };

  const openEditCase = (tc) => {
    setEditingCase(tc);
    const exp = tc.expected || {};
    setForm({
      name: tc.name || "",
      description: tc.description || "",
      ocrTestCompanyId: String(tc.ocrTestCompanyId || ""),
      scenarioTag: tc.scenarioTag || "",
      file: null,
      expected: EXPECTED_MONEY_FIELDS.reduce((acc, [k]) => {
        if (exp[k] !== undefined && exp[k] !== null) acc[k] = String(exp[k]);
        return acc;
      }, {}),
      docType: exp.docType || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm(null);
    setEditingCase(null);
  };

  const setFormField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setExpectedField = (k, v) =>
    setForm((f) => ({ ...f, expected: { ...f.expected, [k]: v } }));

  // Classification routing path is no longer manually authored — it's derived
  // from the fixture's file type + the live classifier kill-switch state,
  // since that's what the real classifier does (never a free admin choice).
  const isPdfName = (name) => !!name && name.toLowerCase().endsWith(".pdf");

  const computedRoutingLabel = () => {
    const name = form?.file?.name || editingCase?.fileUrl || "";
    if (!name) return "Upload a fixture to see the expected routing path.";
    if (isPdfName(name)) return "Azure only (PDF — classification is skipped)";
    if (!classifierConfig) return "—";
    return classifierConfig.killSwitch
      ? "Qwen only (kill switch is ON — Azure is skipped)"
      : "Azure + Qwen (parallel — kill switch is OFF)";
  };

  // Assemble the `expected` Partial<IReceipt> from the modal's scalar inputs.
  const buildExpected = () => {
    const out = {};
    for (const [k] of EXPECTED_MONEY_FIELDS) {
      const raw = form.expected[k];
      if (raw !== undefined && raw !== "" && !Number.isNaN(Number(raw))) {
        out[k] = Number(raw);
      }
    }
    if (form.docType) {
      out.docType = form.docType;
    }
    return out;
  };

  const saveCase = async (thenRun) => {
    if (!form.name.trim()) {
      notifyError("Name is required");
      return;
    }
    if (!form.ocrTestCompanyId) {
      notifyError("Test company is required");
      return;
    }
    if (!editingCase && !form.file) {
      notifyError("Please load an image of receipt or invoice");
      return;
    }
    setSaving(true);
    try {
      // A fixture's company is pinned on create and never re-assigned on edit
      // (the picker is disabled in edit mode). This keeps regionCode and the
      // fileUrl's server-derived S3 subPath in the same region.
      const effectiveCompanyId = editingCase
        ? String(editingCase.ocrTestCompanyId)
        : form.ocrTestCompanyId;
      const company = companyById[effectiveCompanyId];
      let fileUrl = editingCase ? editingCase.fileUrl : undefined;

      // upload path — presign → direct S3 PUT (mirrors mobile ImageUpload.addPhoto)
      if (form.file) {
        const presign = await presignFixtureUpload({
          filename: form.file.name,
          contentType: form.file.type,
          ocrTestCompanyId: effectiveCompanyId,
        });
        const { uploadUrl, publicUrl } = presign.data.data || {};
        if (!uploadUrl || !publicUrl) {
          throw new Error("Presigned upload URL was not returned");
        }
        const put = await putFileToS3(uploadUrl, form.file);
        if (!put.ok) {
          // S3 returns a descriptive XML error body (e.g. <Code>AccessDenied</Code>,
          // <Message>...</Message>) — surface it instead of just the status code.
          let detail = "";
          try {
            const body = await put.text();
            const code = body.match(/<Code>(.*?)<\/Code>/)?.[1];
            const message = body.match(/<Message>(.*?)<\/Message>/)?.[1];
            detail = [code, message].filter(Boolean).join(": ");
          } catch {
            // response body already consumed or unreadable — fall back to status only
          }
          throw new Error(
            `S3 upload failed (${put.status})${detail ? ` — ${detail}` : ""}`
          );
        }
        fileUrl = publicUrl;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        regionCode: company?.regionCode || null,
        ocrTestCompanyId: effectiveCompanyId,
        fileUrl,
        scenarioTag: form.scenarioTag || null,
        expected: buildExpected(),
      };

      let savedCase;
      if (editingCase) {
        const res = await updateCase(editingCase._id, payload);
        savedCase = res.data.data;
      } else {
        const res = await createCase(payload);
        savedCase = res.data.data;
      }

      notifyTopRight(editingCase ? "Test case updated" : "Test case created");
      setModalOpen(false);
      setForm(null);
      setEditingCase(null);
      await fetchAll();

      if (thenRun && savedCase?._id) {
        await handleRunSelected([String(savedCase._id)]);
      }
    } catch (err) {
      notifyError(
        err?.response?.data?.message || err?.message || "Failed to save test case"
      );
    } finally {
      setSaving(false);
    }
  };

  // runs grouped by testCaseId, newest first (listRuns already sorts desc)
  const runsByCase = useMemo(() => {
    const m = {};
    for (const r of runs) {
      const k = String(r.testCaseId);
      if (!m[k]) m[k] = [];
      m[k].push(r);
    }
    return m;
  }, [runs]);

  const companyById = useMemo(() => {
    const m = {};
    for (const c of companies) m[String(c._id)] = c;
    return m;
  }, [companies]);

  // region chips populated dynamically from the Test Companies registry (PLAN §7)
  const regionCodes = useMemo(() => {
    const set = new Set();
    for (const c of companies) if (c.regionCode) set.add(c.regionCode);
    for (const tc of cases) if (tc.regionCode) set.add(tc.regionCode);
    return Array.from(set).sort();
  }, [companies, cases]);

  // region + company filtered — the stats cards count against this set
  const scopedCases = useMemo(() => {
    return cases.filter((tc) => {
      if (regionFilter !== "all" && tc.regionCode !== regionFilter) return false;
      if (
        companyFilter !== "all" &&
        String(tc.ocrTestCompanyId) !== String(companyFilter)
      )
        return false;
      return true;
    });
  }, [cases, regionFilter, companyFilter]);

  const caseStatus = (tc) => {
    const st = lastRunStatus(runsByCase[String(tc._id)]);
    if (st === "pass") return "pass";
    if (st === "fail" || st === "errored") return "fail";
    return "neverRun";
  };

  // additionally narrowed by the active stats-card filter — this drives the table
  const visibleCases = useMemo(() => {
    if (statusFilter === "all") return scopedCases;
    if (statusFilter === "taxReview") {
      return scopedCases.filter((tc) =>
        latestRunNeedsTaxReview(runsByCase[String(tc._id)])
      );
    }
    return scopedCases.filter((tc) => caseStatus(tc) === statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedCases, statusFilter, runsByCase]);

  const visibleIds = useMemo(
    () => visibleCases.map((tc) => String(tc._id)),
    [visibleCases]
  );
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleAll = () => {
    setSelected(allVisibleSelected ? [] : visibleIds);
  };

  // ── stats strip ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let pass = 0;
    let fail = 0;
    let neverRun = 0;
    let taxReview = 0;
    for (const tc of scopedCases) {
      const runsForCase = runsByCase[String(tc._id)];
      const st = lastRunStatus(runsForCase);
      if (st === "pass") pass += 1;
      else if (st === "fail" || st === "errored") fail += 1;
      else neverRun += 1;
      if (latestRunNeedsTaxReview(runsForCase)) taxReview += 1;
    }
    return { total: scopedCases.length, pass, fail, neverRun, taxReview };
  }, [scopedCases, runsByCase]);

  // ── coverage matrix (client-side join, PLAN decision 3) ──────────────────
  const coverage = useMemo(() => {
    // cell[scenario][region] = 'pass' | 'fail' | null
    const cell = {};
    for (const code of SCENARIO_CODES) cell[code] = {};
    for (const tc of cases) {
      if (!tc.isActive) continue;
      const code = tc.scenarioTag;
      const region = tc.regionCode;
      if (!code || !region || !cell[code]) continue;
      const st = lastRunStatus(runsByCase[String(tc._id)]);
      const prev = cell[code][region];
      // pass wins over fail wins over null
      if (st === "pass") cell[code][region] = "pass";
      else if (st === "fail" || st === "errored") {
        if (prev !== "pass") cell[code][region] = "fail";
      } else if (prev === undefined) {
        cell[code][region] = null;
      }
    }
    return cell;
  }, [cases, runsByCase]);

  const [matrixOpen, setMatrixOpen] = useState(false);

  // ── detail-view derived data ───────────────────────────────────────────
  const detailCase = useMemo(
    () => cases.find((tc) => String(tc._id) === String(detailCaseId)) || null,
    [cases, detailCaseId]
  );
  const detailRuns = useMemo(
    () => runsByCase[String(detailCaseId)] || [],
    [runsByCase, detailCaseId]
  );
  const detailRun = useMemo(() => {
    if (!detailRuns.length) return null;
    if (detailRunId)
      return detailRuns.find((r) => String(r._id) === String(detailRunId)) || null;
    return detailRuns[0]; // latest
  }, [detailRuns, detailRunId]);

  const approveDetailRun = async () => {
    if (!detailRun?._id || approving) return;
    setApproving(true);
    try {
      await approveRun(detailRun._id);
      notifyTopRight("Run approved as the new expected");
      await fetchAll();
    } catch (err) {
      notifyError(
        err?.response?.data?.message || "Failed to approve run as expected"
      );
    } finally {
      setApproving(false);
    }
  };

  const coverageDot = (state) => {
    const color =
      state === "pass" ? "#2bc155" : state === "fail" ? "#f72b50" : "#d5d6dc";
    return (
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
        }}
      />
    );
  };

  // ── render: DETAIL view ─────────────────────────────────────────────────
  if (view === "detail") {
    const co = detailCase
      ? companyById[String(detailCase.ocrTestCompanyId)]
      : null;
    const activeDiff =
      detailRun && detailRun.hub ? detailRun.hub.diff || [] : [];
    const grouped = GROUP_ORDER.reduce((acc, g) => {
      acc[g] = activeDiff.filter((d) => fieldGroup(d.field) === g);
      return acc;
    }, {});
    // Stage 5 (§8) — Layer 2b, the mobile rendering check. `hub.mobileRenderDiff`
    // is null when the bridge did not run for this run — the tab shows a muted
    // "not captured" line in that case instead of a second grouped table.
    const mobileRenderDiff = detailRun?.hub?.mobileRenderDiff || null;
    const mobileRenderGrouped = mobileRenderDiff
      ? GROUP_ORDER.reduce((acc, g) => {
          acc[g] = mobileRenderDiff.filter((d) => fieldGroup(d.field) === g);
          return acc;
        }, {})
      : null;
    const rawForTab =
      detailTab === "extraction"
        ? detailRun?.rawExtraction
        : detailTab === "conversion"
        ? detailRun?.actual
        : detailRun?.hub?.savedReceiptRecord;

    // per-tab at-a-glance pill (design shows "Extraction (2 fail)" etc.)
    // Folds Layer 2b (mobileRenderDiff) fails into the count when present (§8).
    const mobileFailCount =
      activeDiff.filter((d) => !d.pass).length +
      (mobileRenderDiff ? mobileRenderDiff.filter((d) => !d.pass).length : 0);
    const tabPill = (tabId) => {
      if (!detailRun) return null;
      if (tabId === "mobile") {
        if (activeDiff.length === 0 && !mobileRenderDiff) {
          return { text: "not scored", tone: "muted" };
        }
        return mobileFailCount > 0
          ? { text: `${mobileFailCount} fail`, tone: "danger" }
          : { text: "all pass", tone: "success" };
      }
      // Extraction (L0) / Conversion (L1) are captured, not scored
      const raw =
        tabId === "extraction" ? detailRun.rawExtraction : detailRun.actual;
      return raw ? { text: "captured", tone: "muted" } : { text: "—", tone: "muted" };
    };

    return (
      <div>
        <PageTitle activeMenu="Run Detail" motherMenu="OCR Test Suite" />
        <Col>
          <Card>
            <Card.Header className="d-block">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={backToList}
              >
                &larr; Back to test cases
              </Button>
              <div className="mt-3 d-flex align-items-start" style={{ gap: 16 }}>
                {detailCase?.fileUrl && (
                  <a
                    href={detailCase.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    title="Open source document in S3"
                    style={{
                      display: "block",
                      width: 78,
                      height: 60,
                      borderRadius: 6,
                      border: "1px solid #f0f1f5",
                      backgroundImage:
                        "repeating-linear-gradient(135deg,#f0f1f5 0 3px,#ffffff 3px 7px)",
                      flex: "0 0 auto",
                    }}
                  />
                )}
                <div>
                  <h5 className="mb-1">
                    {detailCase?.name || "—"}{" "}
                    {detailCase?.regionCode && (
                      <Badge variant="info light">
                        {detailCase.regionCode}
                      </Badge>
                    )}{" "}
                    {detailCase?.scenarioTag && (
                      <Badge variant="dark light">
                        scenario_{detailCase.scenarioTag}
                      </Badge>
                    )}
                  </h5>
                  <p className="mb-1 text-muted" style={{ fontSize: 12 }}>
                    ran against <strong>{co?.label || "—"}</strong>
                  </p>
                  {detailRun ? (
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      run {String(detailRun._id).slice(-6)} ·{" "}
                      {formatDate(detailRun.runAt || detailRun.createdAt)} ·{" "}
                      {detailRun.triggeredBy} ·{" "}
                      {detailRun.status === "running"
                        ? "running…"
                        : detailRun.status === "errored"
                        ? "errored"
                        : detailRun.overallPass
                        ? "PASS"
                        : "FAIL"}
                    </p>
                  ) : (
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      No runs yet for this test case.
                    </p>
                  )}
                  {detailRun?.errorMessage && (
                    <p className="mb-0 text-danger" style={{ fontSize: 12 }}>
                      {detailRun.errorMessage}
                    </p>
                  )}
                </div>
              </div>
            </Card.Header>

            {detailRun && (
              <Card.Body>
                {/* run picker */}
                {detailRuns.length > 1 && (
                  <Form.Group>
                    <Form.Label style={{ fontSize: 12 }}>Run</Form.Label>
                    <Form.Control
                      as="select"
                      style={{ width: "auto" }}
                      value={detailRunId || String(detailRuns[0]._id)}
                      onChange={(e) => setDetailRunId(e.target.value)}
                    >
                      {detailRuns.map((r) => (
                        <option key={r._id} value={r._id}>
                          {formatDate(r.runAt || r.createdAt)} —{" "}
                          {r.status === "done"
                            ? r.overallPass
                              ? "pass"
                              : "fail"
                            : r.status}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}

                {/* tabs */}
                <div
                  className="d-flex"
                  style={{ gap: 4, borderBottom: "1px solid #f0f1f5" }}
                >
                  {[
                    ["extraction", "Extraction (Layer 0)"],
                    ["conversion", "Conversion (Layer 1)"],
                    ["mobile", "Mobile Presentation (Layer 2a)"],
                  ].map(([id, label]) => {
                    const pill = tabPill(id);
                    const toneColor =
                      pill?.tone === "danger"
                        ? "#f72b50"
                        : pill?.tone === "success"
                        ? "#2bc155"
                        : "#7e7e7e";
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setDetailTab(id)}
                        style={{
                          padding: "10px 14px",
                          border: "none",
                          background: "transparent",
                          borderBottom:
                            detailTab === id
                              ? "2px solid #2f4cdd"
                              : "2px solid transparent",
                          fontWeight: detailTab === id ? 600 : 400,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                        {pill && (
                          <span
                            style={{
                              marginLeft: 8,
                              padding: "1px 7px",
                              borderRadius: 9,
                              fontSize: 10,
                              fontWeight: 500,
                              background: `${toneColor}22`,
                              color: toneColor,
                            }}
                          >
                            {pill.text}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3">
                  {detailTab === "mobile" ? (
                    <>
                    {GROUP_ORDER.map((g) => {
                      const gRows = grouped[g];
                      const gFail = gRows.filter((d) => !d.pass).length;
                      return (
                      <div key={g} className="mb-3">
                        <h6 className="text-muted">
                          {g}
                          <span
                            className="ml-2"
                            style={{ fontSize: 11, fontWeight: 400 }}
                          >
                            {gRows.length} field{gRows.length === 1 ? "" : "s"}
                            {gRows.length > 0 &&
                              ` · ${gRows.length - gFail} pass, ${gFail} fail`}
                          </span>
                        </h6>
                        {gRows.length === 0 ? (
                          <p className="text-muted" style={{ fontSize: 12 }}>
                            No scored fields in this group.
                          </p>
                        ) : (
                          <Table responsive size="sm">
                            <thead className="text-black">
                              <tr>
                                <th>
                                  <strong>FIELD</strong>
                                </th>
                                <th>
                                  <strong>EXPECTED</strong>
                                </th>
                                <th>
                                  <strong>ACTUAL</strong>
                                </th>
                                <th>
                                  <strong>✓/✗</strong>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {grouped[g].map((d, i) => (
                                <tr
                                  key={i}
                                  style={{
                                    background: d.pass
                                      ? "transparent"
                                      : "rgba(247,43,80,.06)",
                                  }}
                                >
                                  <td>{d.field}</td>
                                  <td style={{ fontFamily: "monospace" }}>
                                    {fmtVal(d.expected)}
                                  </td>
                                  <td
                                    style={{
                                      fontFamily: "monospace",
                                      color: d.pass ? undefined : "#f72b50",
                                    }}
                                  >
                                    {fmtVal(d.actual)}
                                  </td>
                                  <td>{d.pass ? "✓" : "✗"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                      );
                    })}

                    {/* Stage 5 (§8) — Layer 2b: mobile's actual rendered values,
                        same grouping/scoring as Layer 2a above. */}
                    <h6 className="text-muted mt-4" style={{ borderTop: "1px solid #f0f1f5", paddingTop: 12 }}>
                      Layer 2b — rendered vs. expected
                    </h6>
                    {!mobileRenderGrouped ? (
                      <p className="text-muted" style={{ fontSize: 12 }}>
                        Layer 2b not captured for this run
                        {detailRun?.hub?.mobileRender?._harnessError
                          ? ` (${detailRun.hub.mobileRender._harnessError})`
                          : ""}
                        .
                      </p>
                    ) : (
                      GROUP_ORDER.map((g) => {
                        const gRows = mobileRenderGrouped[g];
                        const gFail = gRows.filter((d) => !d.pass).length;
                        return (
                        <div key={`2b-${g}`} className="mb-3">
                          <h6 className="text-muted">
                            {g}
                            <span
                              className="ml-2"
                              style={{ fontSize: 11, fontWeight: 400 }}
                            >
                              {gRows.length} field{gRows.length === 1 ? "" : "s"}
                              {gRows.length > 0 &&
                                ` · ${gRows.length - gFail} pass, ${gFail} fail`}
                            </span>
                          </h6>
                          {gRows.length === 0 ? (
                            <p className="text-muted" style={{ fontSize: 12 }}>
                              No scored fields in this group.
                            </p>
                          ) : (
                            <Table responsive size="sm">
                              <thead className="text-black">
                                <tr>
                                  <th>
                                    <strong>FIELD</strong>
                                  </th>
                                  <th>
                                    <strong>EXPECTED</strong>
                                  </th>
                                  <th>
                                    <strong>RENDERED</strong>
                                  </th>
                                  <th>
                                    <strong>✓/✗</strong>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {gRows.map((d, i) => (
                                  <tr
                                    key={i}
                                    style={{
                                      background: d.pass
                                        ? "transparent"
                                        : "rgba(247,43,80,.06)",
                                    }}
                                  >
                                    <td>{d.field}</td>
                                    <td style={{ fontFamily: "monospace" }}>
                                      {fmtVal(d.expected)}
                                    </td>
                                    <td
                                      style={{
                                        fontFamily: "monospace",
                                        color: d.pass ? undefined : "#f72b50",
                                      }}
                                    >
                                      {fmtVal(d.actual)}
                                    </td>
                                    <td>{d.pass ? "✓" : "✗"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                        );
                      })
                    )}
                    </>
                  ) : (
                    <p className="text-muted" style={{ fontSize: 12 }}>
                      {detailTab === "extraction"
                        ? "Raw Azure Document Intelligence + Qwen response (Layer 0). Not scored — captured for run-to-run comparison."
                        : "Parsed ExtractedData (Layer 1). Not scored against expected; snapshot for diffRuns."}
                    </p>
                  )}

                  {detailTab === "mobile" &&
                    detailRun.hubVsJobLinkedDiff &&
                    detailRun.hubVsJobLinkedDiff.length > 0 && (
                      <div className="mb-3">
                        <h6 className="text-muted">
                          Hub vs. job-linked (read-path parity)
                        </h6>
                        <Table responsive size="sm">
                          <thead className="text-black">
                            <tr>
                              <th>
                                <strong>FIELD</strong>
                              </th>
                              <th>
                                <strong>HUB</strong>
                              </th>
                              <th>
                                <strong>JOB-LINKED</strong>
                              </th>
                              <th>
                                <strong>✓/✗</strong>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailRun.hubVsJobLinkedDiff.map((d, i) => (
                              <tr
                                key={i}
                                style={{
                                  background: d.pass
                                    ? "transparent"
                                    : "rgba(247,43,80,.06)",
                                }}
                              >
                                <td>{d.field}</td>
                                <td style={{ fontFamily: "monospace" }}>
                                  {fmtVal(d.hubValue)}
                                </td>
                                <td style={{ fontFamily: "monospace" }}>
                                  {fmtVal(d.jobLinkedValue)}
                                </td>
                                <td>{d.pass ? "✓" : "✗"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setRawOpen((o) => !o)}
                    >
                      {rawOpen ? "Hide" : "Show"} full raw JSON
                    </Button>
                    {detailTab === "conversion" && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={approveDetailRun}
                        disabled={approving || detailRun.status !== "done"}
                      >
                        {approving ? "Approving…" : "Approve as Expected"}
                      </Button>
                    )}
                  </div>

                  {rawOpen && (
                    <pre
                      style={{
                        background: "#f8f9fa",
                        padding: 12,
                        borderRadius: 4,
                        maxHeight: 360,
                        overflow: "auto",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        marginTop: 10,
                      }}
                    >
                      {rawForTab
                        ? JSON.stringify(rawForTab, null, 2)
                        : "(nothing captured for this layer)"}
                    </pre>
                  )}
                </div>
              </Card.Body>
            )}
          </Card>

          {/* run history */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">Run History</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive size="sm">
                <thead className="text-black">
                  <tr>
                    <th>
                      <strong>TIMESTAMP</strong>
                    </th>
                    <th>
                      <strong>TRIGGERED BY</strong>
                    </th>
                    <th>
                      <strong>FAILING FIELDS</strong>
                    </th>
                    <th>
                      <strong>RESULT</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detailRuns.map((r) => {
                    const failing = (r.hub?.diff || [])
                      .filter((d) => !d.pass)
                      .map((d) => d.field);
                    return (
                      <tr
                        key={r._id}
                        style={{ cursor: "pointer" }}
                        onClick={() => setDetailRunId(String(r._id))}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>
                          {formatDate(r.runAt || r.createdAt)}
                        </td>
                        <td>{r.triggeredBy}</td>
                        <td style={{ fontSize: 12 }}>
                          {r.status !== "done"
                            ? "—"
                            : failing.length
                            ? failing.join(", ")
                            : "—"}
                        </td>
                        <td>
                          {r.status === "running"
                            ? statusBadge("running")
                            : r.status === "errored"
                            ? statusBadge("errored")
                            : statusBadge(r.overallPass ? "pass" : "fail")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {detailRuns.length === 0 && (
                <div className="text-muted">No runs recorded.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </div>
    );
  }

  // ── render: LIST view ───────────────────────────────────────────────────
  return (
    <div>
      <PageTitle activeMenu="OCR Test Suite" />
      <Col>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted" style={{ fontSize: 12 }}>
            {classifierConfig ? (
              <>
                Classifier kill switch:{" "}
                <strong>{classifierConfig.killSwitch ? "ON" : "OFF"}</strong>{" "}
                —{" "}
                {classifierConfig.killSwitch
                  ? "images are classified by Qwen only; Azure is skipped."
                  : "images are classified by Azure and Qwen in parallel."}{" "}
                PDFs always skip classification and use Azure only,
                regardless of this setting. Change it on Classifier Settings.
              </>
            ) : (
              "Loading classifier config…"
            )}
          </div>
          <Button variant="outline-primary" size="sm" onClick={openNewCase}>
            + New Test Case
          </Button>
        </div>

        {/* stats strip — click a card to filter the table by that status */}
        <div className="row">
          {[
            { key: "all", label: "FIXTURES", value: stats.total },
            { key: "pass", label: "PASSING", value: stats.pass },
            { key: "fail", label: "FAILING", value: stats.fail },
            { key: "neverRun", label: "NEVER RUN", value: stats.neverRun },
            {
              key: "taxReview",
              label: "NEEDS TAX REVIEW",
              value: stats.taxReview,
            },
          ].map((s) => {
            const active = statusFilter === s.key;
            return (
              <div className="col-xl col-md-4 col-sm-6" key={s.label}>
                <Card
                  style={{
                    cursor: "pointer",
                    border: active ? "1px solid #2f4cdd" : undefined,
                    boxShadow: active
                      ? "0 0 0 3px rgba(47,76,221,.12)"
                      : undefined,
                  }}
                  onClick={() =>
                    setStatusFilter(active ? "all" : s.key)
                  }
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-1 text-muted" style={{ fontSize: 11 }}>
                        {s.label}
                      </p>
                      {active && s.key !== "all" && (
                        <span style={{ fontSize: 10, color: "#2f4cdd" }}>
                          FILTERING
                        </span>
                      )}
                    </div>
                    <h3 className="mb-0">{s.value}</h3>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </div>

        {/* coverage matrix */}
        <Card>
          <Card.Header className="d-block">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Scenario Coverage</h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setMatrixOpen((o) => !o)}
              >
                {matrixOpen ? "Hide" : "Show"}
              </Button>
            </div>
          </Card.Header>
          {matrixOpen && (
            <Card.Body>
              {regionCodes.length === 0 ? (
                <div className="text-muted">No regions seeded yet.</div>
              ) : (
                <Table responsive size="sm">
                  <thead className="text-black">
                    <tr>
                      <th>
                        <strong>SCENARIO</strong>
                      </th>
                      {regionCodes.map((r) => (
                        <th key={r} style={{ textAlign: "center" }}>
                          <strong>{r}</strong>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {SCENARIO_CODES.map((code) => (
                      <tr key={code}>
                        <td title={SCENARIO_DESCRIPTIONS[code]}>
                          scenario_{code}
                        </td>
                        {regionCodes.map((r) => (
                          <td key={r} style={{ textAlign: "center" }}>
                            {code in coverage && r in coverage[code]
                              ? coverageDot(coverage[code][r])
                              : coverageDot(null)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                {coverageDot("pass")} passing &nbsp; {coverageDot("fail")} failing
                &nbsp; {coverageDot(null)} no fixture yet
              </div>
            </Card.Body>
          )}
        </Card>

        {/* test cases */}
        <Card>
          <Card.Header className="d-block">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="mb-0">Test Cases</h5>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                {/* region chips */}
                <Button
                  variant={regionFilter === "all" ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setRegionFilter("all")}
                >
                  All
                </Button>
                {regionCodes.map((r) => (
                  <Button
                    key={r}
                    variant={regionFilter === r ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => setRegionFilter(r)}
                  >
                    {r}
                  </Button>
                ))}
                {/* header company filter — filter only, never sets a fixture's company */}
                <select
                  className="form-control form-control-sm"
                  style={{ width: "auto", display: "inline-block" }}
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <option value="all">All companies</option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.label || c.regionCode || c._id}
                    </option>
                  ))}
                </select>
                {/* run selected */}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={selectedVisible.length === 0 || running}
                  onClick={() => handleRunSelected()}
                >
                  {running
                    ? runProgress || "Running…"
                    : `Run ${selectedVisible.length} selected`}
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {loader ? (
              <Spinner />
            ) : (
              <Table responsive>
                <thead className="text-black">
                  <tr>
                    <th style={{ width: 34 }}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleAll}
                        title="Select all visible"
                      />
                    </th>
                    <th>
                      <strong>IMAGE</strong>
                    </th>
                    <th>
                      <strong>NAME</strong>
                    </th>
                    <th>
                      <strong>TEST COMPANY</strong>
                    </th>
                    <th>
                      <strong>SCENARIO</strong>
                    </th>
                    <th>
                      <strong>LAST RUN</strong>
                    </th>
                    <th>
                      <strong>UPDATED</strong>
                    </th>
                    <th>
                      <strong>ACTIONS</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCases.map((tc) => {
                    const co = companyById[String(tc.ocrTestCompanyId)];
                    const runsForCase = runsByCase[String(tc._id)];
                    const st = lastRunStatus(runsForCase);
                    const needsTaxReview = latestRunNeedsTaxReview(runsForCase);
                    const isSel = selected.includes(String(tc._id));
                    return (
                      <tr key={tc._id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSelected(String(tc._id))}
                          />
                        </td>
                        <td>
                          {tc.fileUrl ? (
                            <a
                              href={tc.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="Open source document in S3"
                              style={{
                                display: "block",
                                width: 44,
                                height: 34,
                                borderRadius: 4,
                                border: "1px solid #f0f1f5",
                                backgroundImage:
                                  "repeating-linear-gradient(135deg,#f0f1f5 0 3px,#ffffff 3px 7px)",
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div
                            style={{ color: "#2f4cdd", cursor: "pointer" }}
                            onClick={() => openDetail(tc._id)}
                          >
                            {tc.name || "—"}
                          </div>
                          {tc.description && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {tc.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <div>{co?.label || "—"}</div>
                          {tc.regionCode && (
                            <Badge variant="info light">{tc.regionCode}</Badge>
                          )}
                        </td>
                        <td>
                          {tc.scenarioTag ? (
                            <Badge variant="dark light">
                              scenario_{tc.scenarioTag}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {statusBadge(st)}
                          {needsTaxReview && (
                            <Badge
                              variant="warning light"
                              className="ml-1"
                              title="Latest run's receipt is flagged needsTaxReview"
                            >
                              tax review
                            </Badge>
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {formatDate(tc.updatedAt)}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="mr-1"
                            onClick={() => openDetail(tc._id)}
                          >
                            View
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => openEditCase(tc)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {visibleCases.length === 0 && !loader && (
              <div className="justify-content-center d-flex my-5">
                No test cases found.
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* New / Edit Test Case modal */}
      <Modal show={modalOpen} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCase ? "Edit Test Case" : "New Test Case"}
          </Modal.Title>
        </Modal.Header>
        {form && (
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>
                  Receipt / invoice file{" "}
                  {editingCase && (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      (leave empty to keep the current fixture)
                    </span>
                  )}
                </Form.Label>
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,application/pdf"
                  onChange={(e) =>
                    setFormField("file", e.target.files?.[0] || null)
                  }
                />
                {editingCase?.fileUrl && !form.file && (
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    Current:{" "}
                    <a href={editingCase.fileUrl} target="_blank" rel="noreferrer">
                      {editingCase.fileUrl.split("/").pop()}
                    </a>
                  </div>
                )}
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      value={form.name}
                      onChange={(e) => setFormField("name", e.target.value)}
                      placeholder="e.g. Home Depot — materials receipt"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Test company *</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.ocrTestCompanyId}
                      disabled={!!editingCase}
                      onChange={(e) =>
                        setFormField("ocrTestCompanyId", e.target.value)
                      }
                    >
                      <option value="">— select —</option>
                      {companies
                        .filter((c) => c.isActive !== false)
                        .map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.label || c.regionCode || c._id}
                            {c.regionCode ? ` (${c.regionCode})` : ""}
                          </option>
                        ))}
                    </Form.Control>
                    {editingCase && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        A fixture is pinned to its company — this fixes its
                        region and S3 folder. To move it to another company,
                        delete this one and create a new fixture (the image and
                        expected values don't carry over).
                      </div>
                    )}
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Scenario tag</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.scenarioTag}
                      onChange={(e) =>
                        setFormField("scenarioTag", e.target.value)
                      }
                    >
                      <option value="">— none —</option>
                      {SCENARIO_CODES.map((code) => (
                        <option key={code} value={code}>
                          scenario_{code} — {SCENARIO_DESCRIPTIONS[code]}
                        </option>
                      ))}
                    </Form.Control>
                    {form.scenarioTag && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {SCENARIO_DESCRIPTIONS[form.scenarioTag]}
                      </div>
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      value={form.description}
                      onChange={(e) =>
                        setFormField("description", e.target.value)
                      }
                    />
                  </Form.Group>
                </div>
              </div>

              <hr />
              <p className="text-muted" style={{ fontSize: 12 }}>
                Expected values — optional. Blank fields stay unscored. Line items
                and variance are captured via <em>Approve as Expected</em> after a
                run.
              </p>
              <div className="row">
                {EXPECTED_MONEY_FIELDS.map(([k, label]) => (
                  <div className="col-md-4" key={k}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12 }}>{label}</Form.Label>
                      <Form.Control
                        value={form.expected[k] || ""}
                        onChange={(e) => setExpectedField(k, e.target.value)}
                        placeholder="—"
                      />
                    </Form.Group>
                  </div>
                ))}
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label style={{ fontSize: 12 }}>Doc type</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.docType}
                      onChange={(e) => setFormField("docType", e.target.value)}
                    >
                      {DOC_TYPES.map((d) => (
                        <option key={d || "none"} value={d}>
                          {d || "— none —"}
                        </option>
                      ))}
                    </Form.Control>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Scored against backend extraction only — mobile doesn't
                      render doc type.
                    </div>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label style={{ fontSize: 12 }}>
                      Classification routing path
                    </Form.Label>
                    <div style={{ fontSize: 12, paddingTop: 6 }}>
                      {computedRoutingLabel()}
                    </div>
                  </Form.Group>
                </div>
              </div>
            </Form>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => saveCase(false)}
            disabled={saving}
          >
            {saving ? "Saving…" : editingCase ? "Save changes" : "Save"}
          </Button>
          <Button
            variant="primary"
            onClick={() => saveCase(true)}
            disabled={saving || running}
          >
            {saving ? "Saving…" : "Save & Run"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
